import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import MainLayout from "../layouts/MainLayout";
import { favoriteService } from "../services/favoriteService";
import { useAuth } from "../contexts/AuthContext";
import { useAppContext } from "../store/AppContext";
import LocationCard from "../components/LocationCard";

export default function SharedFavoritesPage() {
  const { code } = useParams();
  const { token, isAuthenticated } = useAuth();
  const { isFavorite, toggleFavorite } = useAppContext();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState("");

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setError("");
      setData(null);
      try {
        const res = await favoriteService.getShare(code);
        if (!mounted) return;
        setData(res || null);
      } catch (e) {
        if (!mounted) return;
        setError(e?.message || "Không tải được link chia sẻ");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [code]);

  const locations = useMemo(() => (Array.isArray(data?.locations) ? data.locations : []), [data]);

  const doImport = async () => {
    if (!isAuthenticated || !token) return;
    setImporting(true);
    setImportMsg("");
    try {
      const res = await favoriteService.importShareToMe(token, code);
      setImportMsg(res?.message || `Đã nhập ${Number(res?.addedCount || 0)} địa điểm`);
    } catch (e) {
      setImportMsg(e?.message || "Nhập danh sách thất bại");
    } finally {
      setImporting(false);
    }
  };

  return (
    <MainLayout>
      <div className="min-h-screen bg-white">
        <div className="mx-auto max-w-7xl px-4 py-10 md:px-8">
          <div className="rounded-2xl border border-[#d8cfbf] bg-white p-6 shadow-sm">
            <p className="text-xs font-bold uppercase tracking-wider text-[#7a7467]">Chia sẻ</p>
            <h1 className="mt-2 text-2xl font-extrabold text-[#2f3a31]">Danh sách địa điểm đã lưu</h1>
            <p className="mt-2 text-sm text-[#5f675b]">
              Link: <span className="font-mono">{String(code || "").trim()}</span>
            </p>

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Link
                to="/locations"
                className="rounded-xl border border-[#d8cfbf] bg-white px-4 py-2 text-sm font-bold text-[#5f675b] shadow-sm transition hover:border-[#b7c6b5] hover:text-[#5f745f]"
              >
                Xem danh sách địa điểm
              </Link>

              {isAuthenticated ? (
                <button
                  type="button"
                  disabled={importing}
                  onClick={doImport}
                  className="rounded-xl bg-[#3a6665] px-4 py-2 text-sm font-extrabold text-white shadow-sm transition hover:bg-[#2e5250] disabled:opacity-60"
                >
                  {importing ? "Đang nhập..." : "Lưu vào Yêu thích của tôi"}
                </button>
              ) : (
                <Link
                  to="/login"
                  className="rounded-xl bg-[#3a6665] px-4 py-2 text-sm font-extrabold text-white shadow-sm transition hover:bg-[#2e5250]"
                >
                  Đăng nhập để lưu
                </Link>
              )}
            </div>

            {importMsg ? (
              <div className="mt-3 rounded-xl border border-[#d8cfbf] bg-white px-4 py-3 text-sm font-semibold text-[#2f3a31]">
                {importMsg}
              </div>
            ) : null}
          </div>

          {loading ? (
            <div className="mt-8 flex justify-center py-16">
              <div className="h-12 w-12 animate-spin rounded-full border-4 border-[#d9e1d1] border-t-[#3a6665]" />
            </div>
          ) : error ? (
            <div className="mt-8 rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm font-semibold text-red-700">
              {error}
            </div>
          ) : locations.length === 0 ? (
            <div className="mt-8 rounded-2xl border border-[#d8cfbf] bg-white px-6 py-10 text-center">
              <p className="text-sm font-semibold text-[#5f675b]">Danh sách trống hoặc đã bị ẩn.</p>
            </div>
          ) : (
            <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {locations.map((item) => (
                <LocationCard
                  key={String(item._id || item.id || item.slug)}
                  item={{
                    ...item,
                    id: String(item._id || item.id || item.slug),
                    image: item.image || item.cover || item.thumbnail || item.image,
                  }}
                  isFav={typeof isFavorite === "function" ? isFavorite(String(item._id || item.id)) : false}
                  onFav={toggleFavorite}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
}

